import { useEffect, useRef, useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { Heart } from 'lucide-preact'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { WebsterChatMessage } from '@/types/webster'
import { WebsterIcon } from '@/components/aiChat/WebsterIcon'
import { STATUS_MESSAGES, pickRandom } from '@/hooks/useStreamingStatus'
import { StreamingStatusLine, intensifyLabel, formatElapsed } from '@/components/webster/StreamingStatusLine'
import { MessageTokenFooter } from '@/components/webster/MessageTokenFooter'

const TYPING_ROTATE_MS = 8000

interface WebsterChatAreaProps {
    messages: WebsterChatMessage[]
    generating: boolean
    emptyMessage?: ComponentChildren
    suggestThankYouGeneration?: boolean
    onGenerateThankYou?: () => void
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function UserMessage({ message }: { message: WebsterChatMessage }) {
    const attachments = message.attachments ?? []
    return (
        <div className="flex justify-end">
            <div className="max-w-[80%]">
                {message.createdByName && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 text-right">{message.createdByName}</div>
                )}
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-end mb-1">
                        {attachments.map((att, i) => (
                            <div
                                key={`${att.url}-${i}`}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1.5 max-w-[200px]"
                            >
                                <img
                                    src={att.url}
                                    alt={att.fileName || 'attachment'}
                                    className="h-10 w-10 rounded object-cover border border-slate-200 dark:border-slate-600 bg-white shrink-0"
                                />
                                <div className="min-w-0 text-left">
                                    <div className="text-xs text-slate-600 dark:text-slate-300 truncate" title={att.fileName}>
                                        {att.fileName || 'Image'}
                                    </div>
                                    {att.note && (
                                        <div className="text-[11px] text-slate-400 dark:text-slate-500 truncate" title={att.note}>
                                            {att.note}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {message.content && (
                    <div className="bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 rounded-2xl rounded-br-md px-4 py-2 text-sm whitespace-pre-wrap">
                        {message.content}
                    </div>
                )}
                <div className="text-xs text-slate-400 dark:text-slate-500 mt-1 text-right">
                    {formatTime(message.timestamp)}
                </div>
            </div>
        </div>
    )
}


function AssistantMessage({ message }: { message: WebsterChatMessage }) {
    const isStreaming = !!message.streamStatus
    const hasContent = !!message.content

    if (!isStreaming && !hasContent) return null

    return (
        <div className="flex gap-3">
            <div className="shrink-0 mt-0.5">
                <WebsterIcon size={26} state={isStreaming ? 'thinking' : 'aware'} />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
                {isStreaming ? (
                    <StreamingStatusLine status={message.streamStatus!} />
                ) : (
                    <>
                        <div className="text-sm text-slate-800 dark:text-slate-100 prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-headings:my-2 prose-code:text-xs prose-pre:my-2">
                            <Markdown remarkPlugins={[remarkGfm]}>
                                {message.content}
                            </Markdown>
                        </div>
                        <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                            {formatTime(message.timestamp)}
                            <MessageTokenFooter
                                modelId={message.modelId}
                                outputTokens={message.outputTokens}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

function TypingIndicator() {
    // Used in the transient state where the chat is generating but no streaming
    // bubble exists yet (e.g. brief gap before the first SignalR event lands).
    // Mirrors StreamingStatusLine's look so the transition is seamless.
    const [status, setStatus] = useState(() => pickRandom(STATUS_MESSAGES.thinking))
    const startedAtRef = useRef(Date.now())
    const [, setTick] = useState(0)

    useEffect(() => {
        const phraseTicker = setInterval(() => {
            setStatus(pickRandom(STATUS_MESSAGES.thinking))
        }, TYPING_ROTATE_MS)
        const secondTicker = setInterval(() => setTick(t => t + 1), 1000)
        return () => {
            clearInterval(phraseTicker)
            clearInterval(secondTicker)
        }
    }, [])

    const elapsedSeconds = Math.floor((Date.now() - startedAtRef.current) / 1000)
    const label = intensifyLabel('thinking', elapsedSeconds)

    return (
        <div className="flex gap-3">
            <div className="shrink-0 mt-0.5">
                <WebsterIcon size={26} state="thinking" />
            </div>
            <span className="text-sm text-slate-500 dark:text-slate-400 italic animate-pulse pt-0.5">
                {status}
                <span className="not-italic text-xs text-slate-400 dark:text-slate-500 ml-1.5">
                    ({formatElapsed(elapsedSeconds)} · {label})
                </span>
            </span>
        </div>
    )
}

export function WebsterChatArea({
    messages,
    generating,
    emptyMessage,
    suggestThankYouGeneration,
    onGenerateThankYou,
}: WebsterChatAreaProps) {
    const bottomRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages.length, messages, generating])

    if (messages.length === 0 && !generating) {
        return (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
                <div className="max-w-md space-y-3">
                    <WebsterIcon size={48} state="aware" className="mx-auto" />
                    {emptyMessage || (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Type a message and click Generate to get started.
                        </p>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="max-w-3xl mx-auto space-y-5">
                {messages.map(msg =>
                    msg.role === 'user'
                        ? <UserMessage key={msg.id} message={msg} />
                        : <AssistantMessage key={msg.id} message={msg} />
                )}
                {generating && !messages.some(m => m.streamStatus) && <TypingIndicator />}
                {suggestThankYouGeneration && !generating && onGenerateThankYou && (
                    <div className="flex justify-center pt-2">
                        <button
                            type="button"
                            onClick={onGenerateThankYou}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors animate-pulse"
                        >
                            <Heart className="w-4 h-4" />
                            Generate Thank You Page
                        </button>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>
        </div>
    )
}
